import { Box, Grid2 as Grid, Link, Stack, TextField } from "@mui/material"
import React from "react"
import { NexSelect } from "../../components/Fields/NexSelect"
import { Label } from "../../components/Typography/Typography"
import { ProjectAllowedAccess } from "../../models/projects/ProjectAllowedAccess"
import { TrainingStatus, trainingStatusDisplayValues } from "../../models/projects/TrainingStatus"
import { StringUtils } from "../../utils/StringUtils"
import { useProjectEditStore } from "../../zstore/ProjectEditStore"

const currentYear = new Date().getFullYear()

export const ProjectTab = () => {
    const project = useProjectEditStore((state) => state.project)
    const updateProjectDetailsField = useProjectEditStore((state) => state.updateProjectDetailsField)

    const projectDeltekDetails: { label: string; value?: string | number }[] = [
        { label: "Client", value: project?.client_name },
        { label: "Account Manager", value: project?.am_employee?.employee_full_name },
        { label: "Project Manager", value: project?.pm_employee?.employee_full_name },
    ]

    const contractLink = project?.project_detail?.contract_link ?? ""
    const contractLinkIsUrl = StringUtils.validateUrl(contractLink)

    return (
        <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
                <Label>Project Details</Label>
                <TextField
                    label="Description"
                    multiline
                    minRows={4}
                    fullWidth
                    variant="outlined"
                    helperText="Required"
                    value={project?.project_detail?.description || ""}
                    onChange={(e) => updateProjectDetailsField("description", e.target.value || null)}
                    slotProps={{ htmlInput: { "data-testid": "project-description" } }}
                />
            </Grid>
            <Grid size={{ xs: 12 }}>
                <Label>Project Info (Deltek)</Label>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 8, lg: 4 }}>
                        <Stack spacing={3}>
                            {projectDeltekDetails.map((detail, idx) => (
                                <TextField
                                    key={idx}
                                    label={detail.label}
                                    value={detail.value ?? ""}
                                    disabled
                                    variant="outlined"
                                />
                            ))}
                        </Stack>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4, lg: 2 }}>
                        <Stack spacing={3}>
                            <TextField
                                label="Fiscal Year"
                                variant="outlined"
                                type="number"
                                name="fiscal_year"
                                value={project?.project_detail?.fiscal_year ?? currentYear}
                                onChange={(e) => updateProjectDetailsField("fiscal_year", Number(e.target.value))}
                                onBlur={(e) => {
                                    const { min, max } = e.target as HTMLInputElement
                                    let { value } = e.target

                                    if (Number.parseInt(value) < Number.parseInt(min)) {
                                        value = min
                                    } else if (Number.parseInt(value) > Number.parseInt(max)) {
                                        value = max
                                    }

                                    updateProjectDetailsField("fiscal_year", Number(value))
                                }}
                                slotProps={{
                                    htmlInput: { min: "2001", max: currentYear + 1, step: "1" },
                                }}
                            />
                            <NexSelect
                                id={"project-training-status"}
                                fullWidth
                                label="Training Status"
                                value={project?.project_detail?.training_status ?? ""}
                                onChange={(e) => {
                                    if (e.target.value === "") {
                                        updateProjectDetailsField("training_status", null)
                                    } else {
                                        updateProjectDetailsField("training_status", e.target.value as TrainingStatus)
                                    }
                                }}
                                options={Object.keys(TrainingStatus)}
                                displayValues={trainingStatusDisplayValues}
                            />
                            <NexSelect
                                fullWidth
                                label="Allowed Access"
                                disableDefaultValue
                                value={project?.project_detail?.allowed_access ?? ""}
                                onChange={(e) => {
                                    if (e.target.value === "") {
                                        updateProjectDetailsField("allowed_access", undefined)
                                    } else {
                                        updateProjectDetailsField(
                                            "allowed_access",
                                            e.target.value as ProjectAllowedAccess,
                                        )
                                    }
                                }}
                                options={Object.keys(ProjectAllowedAccess)}
                                helperText="Other: Provide more info in description"
                            />
                        </Stack>
                    </Grid>
                    <Grid size={{ xs: 12, lg: 6 }}>
                        <TextField
                            fullWidth={true}
                            label={"Contract Link"}
                            value={contractLink ?? ""}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                updateProjectDetailsField("contract_link", event.target.value)
                            }}
                            error={!contractLinkIsUrl && contractLink != null && contractLink.length > 0}
                            helperText={"Link to Project contract in Share Point"}
                            slotProps={{ htmlInput: { "data-testid": "project-contract-link" } }}
                        />
                        {contractLinkIsUrl && (
                            <Box mt={1}>
                                <Link href={contractLink} sx={{ mt: 10 }} target={"_blank"} rel={"noopener noreferrer"}>
                                    {contractLink}
                                </Link>
                            </Box>
                        )}
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    )
}
